import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { HOTEL_NAME, HOTEL_ADDRESS, HOTEL_PHONE, HOTEL_EMAIL, HOTEL_MOTTO } from "../Variables.js";
import HotelLogo from '../images/hotel_logo.png';

const formatDate = (dateObj) => {
  if (!dateObj) return 'N/A';
  let date;
  if (dateObj.toDate) {
    date = dateObj.toDate();
  } else {
    date = new Date(dateObj);
  }
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const formatCurrency = (amount) => {
  if (!amount) return '0';
  return parseFloat(amount).toLocaleString();
};

const getSafeText = (value, fallback = 'N/A') => {
  return value ? String(value).trim() : fallback;
};

const generateQRCodeDataURL = async (text) => {
  try {
    return await QRCode.toDataURL(text, {
      width: 300,
      margin: 2,
      color: {
        dark: '#181818',
        light: '#FFFFFF'
      }
    });
  } catch (err) {
    console.error('QR code generation failed:', err);
    return null;
  }
};

const generateReservationPDF = async (booking) => {
  const hotelName = `${HOTEL_NAME}`;
  const hotelAddress = `${HOTEL_ADDRESS}`;
  const hotelPhone = `${HOTEL_PHONE}`;
  const hotelEmail = `${HOTEL_EMAIL}`;
  const hotelMotto = `${HOTEL_MOTTO}`;

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  let yPos = margin;

  doc.setTextColor(24, 24, 24);

  // Logo (left)
  try {
    const logoWidth = 35;
    const logoHeight = 35;
    doc.addImage(HotelLogo, 'PNG', margin, yPos, logoWidth, logoHeight);
  } catch (e) {
    // Skip if logo missing
  }

  // Faded watermark
  try {
    doc.setGState(new doc.GState({opacity: 0.3}));
    doc.addImage(HotelLogo, 'PNG', margin + 55, yPos + 50, 200, 200);
    doc.setGState(new doc.GState({opacity: 1}));
  } catch (e) {}

  // Hotel info
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(hotelName, margin + 40, yPos + 10);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(hotelAddress, margin + 40, yPos + 18);
  doc.text(`${hotelPhone} | ${hotelEmail}`, margin + 40, yPos + 24);
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'italic');
  doc.text(hotelMotto, margin + 40, yPos + 30);

  yPos += 45;

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(255, 178, 103);
  doc.text('RESERVATION CONFIRMATION', pageWidth / 2, yPos, { align: 'center' });

  yPos += 5;
  doc.setDrawColor(255, 178, 103);
  doc.setLineWidth(1);
  doc.line(pageWidth / 2 - 60, yPos, pageWidth / 2 + 60, yPos);

  yPos += 10;

  // Columns
  const leftColumnX = margin;
  const rightColumnX = pageWidth / 2 + 10;
  let columnY = yPos;

  // Guest Info
  doc.setTextColor(24, 24, 24);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('GUEST INFORMATION', leftColumnX, columnY);
  columnY += 10;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);

  const guestInfo = [
    `Name: ${getSafeText(booking.guestName)}`,
    `Phone: ${getSafeText(booking.guestPhone)}`,
    `Email: ${getSafeText(booking.guestEmail)}`
  ];

  guestInfo.forEach(line => {
    doc.text(line, leftColumnX, columnY);
    columnY += 8;
  });

  // Booking Details
  columnY = yPos;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('BOOKING DETAILS', rightColumnX, columnY);
  columnY += 10;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);

  const bookingDetails = [
    `Room: ${getSafeText(booking.roomNumber)} (${getSafeText(booking.roomTypeName)})`,
    `Check-in: ${formatDate(booking.checkInDate)}`,
    `Check-out: ${formatDate(booking.checkOutDate)}`,
    `Status: ${booking.status ? booking.status.charAt(0).toUpperCase() + booking.status.slice(1) : 'Pending'}`
  ];

  bookingDetails.forEach(line => {
    doc.text(line, rightColumnX, columnY);
    columnY += 8;
  });

  yPos = columnY;

  // Payment Summary Table
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(255, 178, 103);
  doc.text('PAYMENT SUMMARY', margin, yPos);
  yPos += 5;

  const tableData = [
    ['Nightly Rate', `N${formatCurrency(booking.nightlyRate)}`],
    ['Total Nights', `${booking.nights || '1'}`],
    ['Total Amount', `N${formatCurrency(booking.totalPrice)}`],
    ['Deposit Paid', `N${formatCurrency(booking.deposit)}`],
    ['Balance Due', `N${formatCurrency(booking.balance)}`]
  ];

  let currentY = yPos;
  const col1Width = 100;
  const col2Width = 60;
  const rowHeight = 10;

  tableData.forEach((row, i) => {
    const bgColor = i % 2 === 0 ? [250, 250, 250] : [255, 255, 255];
    doc.setFillColor(...bgColor);
    doc.rect(margin, currentY, col1Width + col2Width, rowHeight, 'F');

    doc.setTextColor(24, 24, 24);
    doc.setFont('helvetica', i === 2 || i === 4 ? 'bold' : 'normal');
    doc.setFontSize(12);
    doc.text(row[0], margin + 5, currentY + 7);
    doc.text(row[1], margin + col1Width + 5, currentY + 7);

    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.rect(margin, currentY, col1Width + col2Width, rowHeight);

    currentY += rowHeight;
  });

  yPos = currentY + 10;

  // === MODIFIED: Extra Amenities with left-right alternating layout ===
  if (booking.selectedAmenities && booking.selectedAmenities.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(255, 178, 103);
    doc.text('EXTRA AMENITIES', margin, yPos);
    yPos += 8;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(24, 24, 24);
    
    const amenities = booking.selectedAmenities.map(amenityId => {
      return booking.amenities?.find(a => a.id === amenityId);
    }).filter(amenity => amenity != null);
    
    const columnWidth = (pageWidth - 2 * margin) / 2;
    const leftX = margin;
    const rightX = margin + columnWidth + 5;
    let leftY = yPos;
    let rightY = yPos;
    
    // Track which column has more content to position next item correctly
    let leftCount = 0;
    let rightCount = 0;
    
    amenities.forEach((amenity, index) => {
      const amenityText = `• ${amenity.name} (+N${formatCurrency(amenity.price)}/night)`;
      
      if (index % 2 === 0) { // Even index: left column (1st, 3rd, 5th, etc.)
        doc.text(amenityText, leftX, leftY);
        leftY += 7;
        leftCount++;
      } else { // Odd index: right column (2nd, 4th, 6th, etc.)
        doc.text(amenityText, rightX, rightY);
        rightY += 7;
        rightCount++;
      }
    });
    
    // Set yPos to the bottom of the longer column
    yPos += Math.max(leftCount, rightCount) * 7 + 5;
  }

  // Notes
  if (booking.notes) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(255, 178, 103);
    doc.text('NOTES', margin, yPos);
    yPos += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(24, 24, 24);
    const splitNotes = doc.splitTextToSize(booking.notes, pageWidth - 2 * margin);
    splitNotes.forEach(line => {
      doc.text(line, margin, yPos);
      yPos += 7;
    });
    yPos += 15;
  }

  // QR Code Section
  const qrText = `Reservation Details\n` +
    `Guest: ${getSafeText(booking.guestName)}\n` +
    `Room: ${getSafeText(booking.roomNumber)}\n` +
    `Check-in: ${formatDate(booking.checkInDate)}\n` +
    `Check-out: ${formatDate(booking.checkOutDate)}\n` +
    `Total: N${formatCurrency(booking.totalPrice)}\n` +
    `Booking ID: ${booking.id || 'N/A'}`;

  const qrDataURL = await generateQRCodeDataURL(qrText);

  if (qrDataURL) {
    const qrSize = 35;
    const qrX = pageWidth - margin - qrSize;
    const qrY = pageHeight - margin - qrSize - 40;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(24, 24, 24);
    doc.text('Scan to View Details', qrX - 7 + (qrSize / 2), pageHeight - 57, { align: 'center' });

    doc.addImage(qrDataURL, 'PNG', qrX - 7, pageHeight - 56, qrSize, qrSize);

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text('Your digital reservation pass', qrX + (qrSize / 2) - 7, pageHeight - 19, { align: 'center' });
  }

  // Footer
  const footerY = pageHeight - 50;
  doc.setDrawColor(255, 178, 103);
  doc.setLineWidth(0.5);
  doc.line(margin, footerY, pageWidth - margin - 60, footerY);

  const generationDateTime = new Date().toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated on ${generationDateTime}`, leftColumnX, footerY + 10 );
  doc.text('Thank you for choosing us!', leftColumnX, footerY + 18);
  doc.text(`© ${new Date().getFullYear()} ${hotelName}. All rights reserved.`, leftColumnX, footerY + 26);

  return doc;
};

const ReservationPDF = {
  downloadPDF: async (booking) => {
    if (!booking) {
      alert("No booking data available");
      return;
    }

    try {
      const doc = await generateReservationPDF(booking);
      const fileName = `Reservation_${getSafeText(booking.guestName).replace(/\s+/g, '_')}_Room${booking.roomNumber}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error("PDF generation failed:", error);
      alert("Failed to generate PDF");
    }
  }
};

export default ReservationPDF;